import { EventEmitter } from 'events';

/**
 * MetricsCache - Real-time metrics calculation and caching service
 * Provides optimized data access and live updates for purchase management metrics
 */
class MetricsCache extends EventEmitter {
  constructor() {
    super();
    this.cache = new Map();
    this.subscriptions = new Map();
    this.updateIntervals = new Map();
    this.pendingCalculations = new Set();
    
    // Cache configuration
    this.config = {
      defaultTTL: 5 * 60 * 1000, // 5 minutes default TTL
      maxCacheSize: 1000,
      updateInterval: 30 * 1000, // 30 seconds default update interval
      batchSize: 10 // Maximum concurrent calculations
    };
    
    // Metrics definitions
    this.metricsDefinitions = {
      purchaseOverview: {
        key: 'purchase_overview',
        ttl: 2 * 60 * 1000, // 2 minutes
        dependencies: ['purchase_orders', 'suppliers'],
        calculator: this.calculatePurchaseOverview.bind(this)
      },
      vendorPerformance: {
        key: 'vendor_performance',
        ttl: 5 * 60 * 1000, // 5 minutes
        dependencies: ['purchase_orders', 'receipts', 'suppliers'],
        calculator: this.calculateVendorPerformance.bind(this)
      },
      costAnalytics: {
        key: 'cost_analytics',
        ttl: 10 * 60 * 1000, // 10 minutes
        dependencies: ['purchase_orders', 'receipts'],
        calculator: this.calculateCostAnalytics.bind(this)
      },
      approvalMetrics: {
        key: 'approval_metrics',
        ttl: 1 * 60 * 1000, // 1 minute
        dependencies: ['purchase_orders'],
        calculator: this.calculateApprovalMetrics.bind(this)
      },
      supplierHealth: {
        key: 'supplier_health',
        ttl: 15 * 60 * 1000, // 15 minutes
        dependencies: ['suppliers', 'purchase_orders', 'receipts'],
        calculator: this.calculateSupplierHealth.bind(this)
      }
    };
    
    this.startCleanupTimer();
  }

  /**
   * Get cached metrics or calculate if not available
   */
  async getMetrics(metricType, params = {}, forceRefresh = false) {
    const cacheKey = this.generateCacheKey(metricType, params);
    const cached = this.cache.get(cacheKey);
    
    // Return cached data if valid and not forcing refresh
    if (!forceRefresh && cached && this.isCacheValid(cached)) {
      this.emit('cache_hit', { metricType, cacheKey });
      return cached.data;
    }
    
    // Calculate metrics if not cached or expired
    return await this.calculateMetrics(metricType, params, cacheKey);
  }

  /**
   * Subscribe to real-time metric updates
   */
  subscribe(metricType, params = {}, callback, updateInterval = null) {
    const cacheKey = this.generateCacheKey(metricType, params);
    const subscriptionKey = `${cacheKey}_${Date.now()}`;
    
    this.subscriptions.set(subscriptionKey, {
      metricType,
      params,
      callback,
      cacheKey,
      lastUpdate: 0
    });
    
    // Set up automatic updates
    const interval = updateInterval || this.config.updateInterval;
    const intervalId = setInterval(async () => {
      try {
        const data = await this.getMetrics(metricType, params, true);
        callback(data);
        this.emit('subscription_update', { subscriptionKey, metricType });
      } catch (error) {
        this.emit('subscription_error', { subscriptionKey, error });
      }
    }, interval);
    
    this.updateIntervals.set(subscriptionKey, intervalId);
    
    // Initial data fetch
    this.getMetrics(metricType, params).then(callback).catch(error => {
      this.emit('subscription_error', { subscriptionKey, error });
    });
    
    return subscriptionKey;
  }

  /**
   * Unsubscribe from metric updates
   */
  unsubscribe(subscriptionKey) {
    this.subscriptions.delete(subscriptionKey);
    
    const intervalId = this.updateIntervals.get(subscriptionKey);
    if (intervalId) {
      clearInterval(intervalId);
      this.updateIntervals.delete(subscriptionKey);
    }
    
    this.emit('unsubscribed', { subscriptionKey });
  }

  /**
   * Invalidate cache for specific metrics
   */
  invalidateCache(metricTypes = null, pattern = null) {
    if (metricTypes) {
      // Invalidate specific metric types
      const types = Array.isArray(metricTypes) ? metricTypes : [metricTypes];
      for (const [key] of this.cache.entries()) {
        const metricType = this.extractMetricType(key);
        if (types.includes(metricType)) {
          this.cache.delete(key);
        }
      }
    } else if (pattern) {
      // Invalidate by pattern
      for (const [key] of this.cache.entries()) {
        if (key.includes(pattern)) {
          this.cache.delete(key);
        }
      }
    } else {
      // Clear all cache
      this.cache.clear();
    }
    
    this.emit('cache_invalidated', { metricTypes, pattern });
  }

  /**
   * Generate cache key from metric type and parameters
   */
  generateCacheKey(metricType, params) {
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((result, key) => {
        result[key] = params[key];
        return result;
      }, {});
    
    return `${metricType}_${JSON.stringify(sortedParams)}`;
  }

  /**
   * Check if cached data is still valid
   */
  isCacheValid(cached) {
    const now = Date.now();
    return (now - cached.timestamp) < cached.ttl;
  }

  /**
   * Extract metric type from cache key
   */
  extractMetricType(cacheKey) {
    return cacheKey.split('_')[0];
  }

  /**
   * Calculate metrics and cache the result
   */
  async calculateMetrics(metricType, params, cacheKey) {
    // Prevent duplicate calculations
    if (this.pendingCalculations.has(cacheKey)) {
      // Wait for ongoing calculation
      return new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          if (!this.pendingCalculations.has(cacheKey)) {
            clearInterval(checkInterval);
            const cached = this.cache.get(cacheKey);
            if (cached) {
              resolve(cached.data);
            } else {
              reject(new Error('Calculation failed'));
            }
          }
        }, 100);
      });
    }

    this.pendingCalculations.add(cacheKey);
    
    try {
      const definition = this.metricsDefinitions[metricType];
      if (!definition) {
        throw new Error(`Unknown metric type: ${metricType}`);
      }

      this.emit('calculation_start', { metricType, cacheKey });
      
      const startTime = Date.now();
      const data = await definition.calculator(params);
      const calculationTime = Date.now() - startTime;
      
      // Cache the result
      this.cache.set(cacheKey, {
        data,
        timestamp: Date.now(),
        ttl: definition.ttl,
        calculationTime
      });
      
      // Cleanup cache if it exceeds max size
      this.cleanupCache();
      
      this.emit('calculation_complete', { 
        metricType, 
        cacheKey, 
        calculationTime,
        dataSize: JSON.stringify(data).length
      });
      
      return data;
      
    } catch (error) {
      this.emit('calculation_error', { metricType, cacheKey, error });
      throw error;
    } finally {
      this.pendingCalculations.delete(cacheKey);
    }
  }

  /**
   * Calculate purchase overview metrics
   */
  async calculatePurchaseOverview(params = {}) {
    
    // Simulate API calls - in real implementation, these would be actual service calls
    const mockData = {
      totalOrders: Math.floor(Math.random() * 1000) + 500,
      totalCost: Math.floor(Math.random() * 5000000) + 1000000,
      averageOrderValue: Math.floor(Math.random() * 50000) + 10000,
      activeSuppliers: Math.floor(Math.random() * 50) + 20,
      pendingApprovals: Math.floor(Math.random() * 20) + 5,
      completedOrders: Math.floor(Math.random() * 800) + 400,
      trends: {
        ordersTrend: (Math.random() - 0.5) * 20,
        costTrend: (Math.random() - 0.5) * 15,
        supplierTrend: (Math.random() - 0.5) * 10
      },
      lastUpdated: new Date().toISOString()
    };
    
    return mockData;
  }

  /**
   * Calculate vendor performance metrics
   */
  async calculateVendorPerformance(params = {}) {
    const { limit = 20 } = params;
    
    const vendors = [];
    for (let i = 0; i < limit; i++) {
      vendors.push({
        supplier_id: `supplier_${i}`,
        supplier_name: `供应商 ${i + 1}`,
        performance_score: Math.floor(Math.random() * 40) + 60,
        delivery_performance: Math.floor(Math.random() * 30) + 70,
        quality_score: Math.floor(Math.random() * 25) + 75,
        cost_efficiency: Math.floor(Math.random() * 35) + 65,
        total_cost: Math.floor(Math.random() * 1000000) + 100000,
        order_count: Math.floor(Math.random() * 50) + 5,
        avg_delivery_days: Math.floor(Math.random() * 15) + 5,
        last_updated: new Date().toISOString()
      });
    }
    
    return {
      vendors: vendors.sort((a, b) => b.performance_score - a.performance_score),
      summary: {
        totalVendors: vendors.length,
        avgPerformance: vendors.reduce((sum, v) => sum + v.performance_score, 0) / vendors.length,
        topPerformer: vendors[0]?.supplier_name || 'N/A'
      },
      lastUpdated: new Date().toISOString()
    };
  }

  /**
   * Calculate cost analytics metrics
   */
  async calculateCostAnalytics(params = {}) {
    return {
      totalCost: Math.floor(Math.random() * 5000000) + 2000000,
      monthlyCost: Math.floor(Math.random() * 800000) + 200000,
      costByCategory: [
        { category: '电子元器件', cost: Math.floor(Math.random() * 500000) + 100000 },
        { category: '机械零件', cost: Math.floor(Math.random() * 400000) + 80000 },
        { category: '原材料', cost: Math.floor(Math.random() * 600000) + 150000 }
      ],
      trends: {
        monthly: Array.from({ length: 12 }, (_, i) => ({
          month: `${i + 1}月`,
          cost: Math.floor(Math.random() * 300000) + 100000
        }))
      },
      lastUpdated: new Date().toISOString()
    };
  }

  /**
   * Calculate approval metrics
   */
  async calculateApprovalMetrics(params = {}) {
    return {
      pendingApprovals: Math.floor(Math.random() * 15) + 3,
      approvedToday: Math.floor(Math.random() * 10) + 2,
      rejectedToday: Math.floor(Math.random() * 3),
      avgApprovalTime: Math.floor(Math.random() * 24) + 12, // hours
      approvalRate: Math.floor(Math.random() * 20) + 80, // percentage
      lastUpdated: new Date().toISOString()
    };
  }

  /**
   * Calculate supplier health metrics
   */
  async calculateSupplierHealth(params = {}) {
    return {
      healthySuppliers: Math.floor(Math.random() * 30) + 20,
      riskSuppliers: Math.floor(Math.random() * 8) + 2,
      criticalSuppliers: Math.floor(Math.random() * 3),
      avgHealthScore: Math.floor(Math.random() * 20) + 75,
      healthDistribution: {
        excellent: Math.floor(Math.random() * 15) + 10,
        good: Math.floor(Math.random() * 20) + 15,
        fair: Math.floor(Math.random() * 10) + 5,
        poor: Math.floor(Math.random() * 5) + 1
      },
      lastUpdated: new Date().toISOString()
    };
  }

  /**
   * Clean up expired cache entries
   */
  cleanupCache() {
    const now = Date.now();
    
    // Remove expired entries
    for (const [key, value] of this.cache.entries()) {
      if (now - value.timestamp > value.ttl) {
        this.cache.delete(key);
      }
    }
    
    // Remove oldest entries if cache is too large
    if (this.cache.size > this.config.maxCacheSize) {
      const entries = Array.from(this.cache.entries())
        .sort((a, b) => a[1].timestamp - b[1].timestamp);
      
      const entriesToRemove = entries.slice(0, this.cache.size - this.config.maxCacheSize);
      entriesToRemove.forEach(([key]) => this.cache.delete(key));
    }
  }

  /**
   * Start automatic cache cleanup
   */
  startCleanupTimer() {
    setInterval(() => {
      this.cleanupCache();
      this.emit('cache_cleanup', { 
        cacheSize: this.cache.size,
        subscriptions: this.subscriptions.size 
      });
    }, 60 * 1000); // Cleanup every minute
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const totalSize = Array.from(this.cache.values())
      .reduce((sum, entry) => sum + JSON.stringify(entry.data).length, 0);
    
    return {
      cacheSize: this.cache.size,
      subscriptions: this.subscriptions.size,
      pendingCalculations: this.pendingCalculations.size,
      totalCacheSize: totalSize,
      maxCacheSize: this.config.maxCacheSize,
      hitRate: this.hitRate || 0
    };
  }

  /**
   * Update cache configuration
   */
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
    this.emit('config_updated', this.config);
  }

  /**
   * Destroy the cache instance
   */
  destroy() {
    // Clear all timers
    for (const intervalId of this.updateIntervals.values()) {
      clearInterval(intervalId);
    }
    
    // Clear all data
    this.cache.clear();
    this.subscriptions.clear();
    this.updateIntervals.clear();
    this.pendingCalculations.clear();
    
    // Remove all listeners
    this.removeAllListeners();
    
    this.emit('destroyed');
  }
}

// Create singleton instance
const metricsCache = new MetricsCache();

// Start the cleanup timer
metricsCache.startCleanupTimer();

export default metricsCache;
export { metricsCache };